import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Phone, Mail, MapPin, Leaf, Clock, Shield, Star } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_xalawnsplus/artifacts/gh7eo36y_a0d14065-cb1f-449b-a4f6-38140797df8a.png";
const HERO_BG = "https://images.unsplash.com/photo-1526392587392-d1627b6c134a?w=1920&q=80";
const SERVICE_IMG_1 = "https://images.unsplash.com/photo-1661133732576-6140941d5421?w=600&q=80";
const SERVICE_IMG_2 = "https://images.unsplash.com/photo-1761637822930-fb1c1a3df94d?w=600&q=80";
const SERVICE_IMG_3 = "https://images.unsplash.com/photo-1759355787144-46638450cb0c?w=600&q=80";

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="XA's Lawns" className="h-14 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-white hover:text-lime-500 transition-colors font-medium">Home</Link>
              <Link to="/contact" className="text-gray-400 hover:text-lime-500 transition-colors font-medium">Contact</Link>
              <Link to="/estimate" className="text-gray-400 hover:text-lime-500 transition-colors font-medium">Get Estimate</Link>
              <Link to="/portal" className="text-gray-400 hover:text-lime-500 transition-colors font-medium">Customer Portal</Link>
              <Link to="/login">
                <Button className="btn-primary" data-testid="nav-login-btn">Admin Login</Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 border-t border-[#262626]">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" className="block text-white hover:text-lime-500 py-2" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/contact" className="block text-gray-400 hover:text-lime-500 py-2" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/estimate" className="block text-gray-400 hover:text-lime-500 py-2" onClick={() => setMobileMenuOpen(false)}>Get Estimate</Link>
              <Link to="/portal" className="block text-gray-400 hover:text-lime-500 py-2" onClick={() => setMobileMenuOpen(false)}>Customer Portal</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button className="btn-primary w-full">Admin Login</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center pt-20"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in">
            <img src={LOGO_URL} alt="XA's Lawns" className="h-32 sm:h-40 mx-auto mb-8" />
            <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-bold text-white uppercase tracking-tight mb-6">
              Professional Care,<br />
              <span className="text-lime-500">Family Values</span>
            </h1>
            <p className="text-gray-300 text-lg sm:text-xl max-w-2xl mx-auto mb-10">
              Quality lawn care services for residential properties. We treat your lawn like it's our own.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/estimate">
                <Button className="btn-primary text-lg" data-testid="hero-estimate-btn">
                  Get Free Estimate
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary text-lg" data-testid="hero-contact-btn">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-lime-500 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-lime-500 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-5xl font-bold text-white uppercase mb-4">
              Our <span className="text-lime-500">Services</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From regular maintenance to complete lawn transformations, we've got you covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Service Card 1 */}
            <div className="feature-card rounded-lg overflow-hidden group" data-testid="service-card-mowing">
              <div className="aspect-video overflow-hidden">
                <img 
                  src={SERVICE_IMG_1} 
                  alt="Lawn Mowing" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-lime-500/20 flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-lime-500" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white uppercase">Lawn Mowing</h3>
                </div>
                <p className="text-gray-400">
                  Professional mowing services tailored to your lawn's needs. Weekly, bi-weekly, or monthly plans available.
                </p>
              </div>
            </div>

            {/* Service Card 2 */}
            <div className="feature-card rounded-lg overflow-hidden group" data-testid="service-card-maintenance">
              <div className="aspect-video overflow-hidden">
                <img 
                  src={SERVICE_IMG_3} 
                  alt="Full Maintenance" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-gold-500" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white uppercase">Full Maintenance</h3>
                </div>
                <p className="text-gray-400">
                  Complete property maintenance including edging, trimming, weed control, leaf removal, and seasonal cleanup.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-5xl font-bold text-white uppercase mb-6">
                Why Choose <span className="text-gold-500">XA's Lawns</span>?
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                We're a family-owned business committed to delivering exceptional lawn care services. Our attention to detail and dedication to customer satisfaction sets us apart.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-lime-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-lime-500" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-bold text-white uppercase mb-1">Reliable Service</h4>
                    <p className="text-gray-400">We show up on time, every time. Count on us for consistent, dependable service.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-gold-500" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-bold text-white uppercase mb-1">Quality First</h4>
                    <p className="text-gray-400">Professional equipment and trained staff ensure top-quality results every visit.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-lime-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-lime-500" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-bold text-white uppercase mb-1">Flexible Plans</h4>
                    <p className="text-gray-400">Per-cut or monthly billing options. We work with your schedule and budget.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-lg overflow-hidden border border-lime-500/30">
                <img 
                  src={SERVICE_IMG_1} 
                  alt="XA's Lawns Quality Work" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-lime-500 p-6 rounded-lg">
                <p className="font-heading text-3xl font-bold text-black">Family</p>
                <p className="text-black/80">Owned & Operated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-5xl font-bold text-white uppercase mb-6">
            Ready to Transform Your <span className="text-lime-500">Lawn</span>?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Get a free estimate today and see why your neighbors trust XA's Lawns for all their lawn care needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimate">
              <Button className="btn-primary text-lg" data-testid="cta-estimate-btn">
                Get Free Estimate
              </Button>
            </Link>
            <a href="tel:+13187066876">
              <Button className="btn-secondary text-lg">
                <Phone className="w-5 h-5 mr-2" />
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#262626] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="XA's Lawns" className="h-16 mb-4" />
              <p className="text-gray-400 mb-4">
                Professional lawn care services for residential properties. Quality work, fair prices, and exceptional service.
              </p>
            </div>

            <div>
              <h4 className="font-heading text-lg font-bold text-white uppercase mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/" className="block text-gray-400 hover:text-lime-500 transition-colors">Home</Link>
                <Link to="/contact" className="block text-gray-400 hover:text-lime-500 transition-colors">Contact</Link>
                <Link to="/estimate" className="block text-gray-400 hover:text-lime-500 transition-colors">Get Estimate</Link>
                <Link to="/portal" className="block text-gray-400 hover:text-lime-500 transition-colors">Customer Portal</Link>
              </div>
            </div>

            <div>
              <h4 className="font-heading text-lg font-bold text-white uppercase mb-4">Contact</h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-lime-500" />
                  <span>(318) 706-6876</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-lime-500" />
                  <span>contact@xaslawns.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-lime-500" />
                  <span>Your Local Area</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#262626] mt-8 pt-8 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} XA's Lawns. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
